import { Input } from "antd";
import React, { useState, type Dispatch, type SetStateAction } from "react";
import { BiChevronDown } from "react-icons/bi";
import styles from "./delegate-creation-card.module.scss";
import ModalPopper from "@/components/layout/modal-popper/modal-popper";
import {
  InputSwitchStateEnum,
  PlatformEnum,
} from "@/types/modular-permissions.types";
import classNames from "classnames";
import { motion } from "framer-motion";
import { resolvePlatformLogo } from "@/utils/resolve-platform-logo.util";
import { platformNameResolver } from "@/utils/platform-name-resolver.util";
import LoadingIcon from "@/components/layout/loading-icon";

interface PlatformInputProps {
  userHandle: string;
  setUserHandle: Dispatch<SetStateAction<string>>;
  selectedPlatform: PlatformEnum | undefined;
  setSelectedPlatform: (platform: PlatformEnum) => void;
  createDelegate: () => void;
  isLoading: boolean;
}

interface WalletInputProps {
  walletAddress: string;
  setWalletAddress: (e: React.ChangeEvent<HTMLInputElement>) => void;
  createDelegate: () => void;
  isLoading: boolean;
}

interface MainInputProps extends WalletInputProps, PlatformInputProps {
  activeInputType: InputSwitchStateEnum;
  createDelegate: () => void;
  isLoading: boolean;
}

const DISABLED_PLATFORMS = [PlatformEnum.Twitter, PlatformEnum.Instagram];

export const CreationInputField = ({
  walletAddress,
  setWalletAddress,
  userHandle,
  activeInputType,
  selectedPlatform,
  setSelectedPlatform,
  setUserHandle,
  createDelegate,
  isLoading,
}: MainInputProps) => {
  if (activeInputType === InputSwitchStateEnum.Platform) {
    return (
      <PlatformInputField
        userHandle={userHandle}
        setUserHandle={setUserHandle}
        selectedPlatform={selectedPlatform}
        setSelectedPlatform={setSelectedPlatform}
        createDelegate={createDelegate}
        isLoading={isLoading}
      />
    );
  } else if (activeInputType === InputSwitchStateEnum.Wallet) {
    return (
      <WalletInputField
        walletAddress={walletAddress}
        setWalletAddress={setWalletAddress}
        createDelegate={createDelegate}
        isLoading={isLoading}
      />
    );
  }
};

const WalletInputField = ({
  walletAddress,
  setWalletAddress,
  createDelegate,
}: WalletInputProps) => {
  return (
    <motion.div
      key={2}
      initial={{ opacity: 0, translateY: 5 }}
      animate={{ opacity: 1, translateY: 0 }}
      transition={{
        bounceStiffness: 0,
        duration: 0.1,
      }}
      exit={{ opacity: 0, translateY: 10 }}
      className={styles.inputWrapper}>
      <input
        aria-label={"Wallet address (e.g., 0x...)"}
        className={styles.inputField}
        placeholder="Wallet address (e.g., 0x...)"
        value={walletAddress}
        onChange={setWalletAddress}
      />

      <button
        className={classNames(
          styles.submitBtn,
          !walletAddress && styles.disabled
        )}
        onClick={createDelegate}
        disabled={!walletAddress}>
        Add permissions
      </button>
    </motion.div>
  );
};

const PlatformInputField = ({
  userHandle,
  setUserHandle,
  selectedPlatform,
  setSelectedPlatform,
  createDelegate,
  isLoading,
}: PlatformInputProps) => {
  const [modalOpen, setModalOpen] = useState(false);

  const handleSetModalOpen = () => {
    setModalOpen((prev) => !prev);
  };

  const handleModalClose = () => {
    setModalOpen(false);
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserHandle(e.target.value);
  };

  const handlePlatformSelect = (platform: PlatformEnum) => {
    setModalOpen(false);
    setSelectedPlatform(platform);
  };

  const SelectedPlatformLogo = resolvePlatformLogo(selectedPlatform);

  return (
    <motion.div
      initial={{ opacity: 0, translateY: 5 }}
      animate={{ opacity: 1, translateY: 0 }}
      transition={{
        bounceStiffness: 0,
        duration: 0.1,
      }}
      className={styles.platformWrapper}>
      <button className={styles.platformSelect} onClick={handleSetModalOpen}>
        {selectedPlatform ? (
          <motion.span
            key={selectedPlatform}
            className={styles.platformSelectedSpan}
            initial={{ opacity: 0, translateY: 10 }}
            animate={{ opacity: 1, translateY: 0 }}>
            {SelectedPlatformLogo && <SelectedPlatformLogo size={15} />}
            {platformNameResolver(selectedPlatform)}
          </motion.span>
        ) : (
          <span className={styles.placeholder}>Select platform</span>
        )}
        <BiChevronDown
          size={18}
          className={classNames(!selectedPlatform && styles.placeholder)}
        />
      </button>

      <div className={styles.inputWrapper}>
        <input
          className={styles.platformField}
          arial-label="Enter user ID..."
          placeholder="Enter user ID..."
          value={userHandle}
          onChange={handleOnChange}
        />
        {isLoading && (
          <div className={styles.loadIcon}>
            <LoadingIcon size={25} />
          </div>
        )}
      </div>
      <button
        className={classNames(styles.submitBtn, !userHandle && styles.disabled)}
        onClick={createDelegate}
        disabled={!userHandle}>
        Add permissions
      </button>
      {/* MODAL FOR PLATFORM SELECT HERE */}
      <ModalPopper isOpen={modalOpen} onClose={handleModalClose}>
        <div className={styles.container}>
          <p className={styles.chainListHeader}>Select a platform</p>
          <ul className={styles.chainList}>
            {Object.values(PlatformEnum).map((platform) => {
              const PlatformIcon = resolvePlatformLogo(platform)!;
              const isDisabled = DISABLED_PLATFORMS.includes(platform);
              return (
                <li
                  key={platform}
                  role="button"
                  aria-disabled={isDisabled}
                  onClick={() => handlePlatformSelect(platform)}
                  className={classNames(
                    platform === selectedPlatform && styles.active,
                    isDisabled && styles.disabled
                  )}>
                  <PlatformIcon className={styles.chainLogo} size={20} />
                  <div className={styles.chainDetails}>
                    <p>{platformNameResolver(platform)}</p>
                  </div>
                  {isDisabled && (
                    <div className={styles.platformDisabled}>Coming soon</div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </ModalPopper>
    </motion.div>
  );
};
